import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import ProductCard from '../components/products/ProductCard';
import { Link } from 'react-router-dom';
import { products, categories, siteImages, steps, performanceBenefits, features, CONTACT_INFO } from '../data/mock';
import { ArrowRight, Truck, ShieldCheck, RotateCcw, Package, Leaf, Award, CheckCircle2, Star } from 'lucide-react';

const featureIcons = [Package, Truck, RotateCcw, ShieldCheck];
const benefitIcons = [Award, Leaf, Package, CheckCircle2];

const HomePage = () => {
  const featuredProduct = products.find(p => p.slug === 'office-chair-with-lumbar-support') || products[0];
  const gridSlugs = ['premium-gel-pen-set','wireless-mouse','high-definition-streaming-webcam','compact-multi-drawer-desk-organizer','desk-organizer-tray','executive-leather-desk-chair','contemporary-office-wall-art-collection','compact-paper-shredder'];
  const productsGrid = gridSlugs.map(s => products.find(p => p.slug === s)).filter(Boolean);

  useEffect(() => { document.title = 'Work Supply House | Office Supplies & Workspace Essentials'; }, []);

  return (
    <div className="min-h-screen bg-background text-dark-700">
      <Header />
      <CartDrawer />
      <main>
        {/* Hero — calm split layout */}
        <section className="bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div className="animate-fade-up">
                <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-athletic-100 text-athletic-800 text-xs font-bold uppercase tracking-[0.16em] rounded-full mb-6"><Leaf className="w-3.5 h-3.5" /> Calm, considered workspaces</span>
                <h1 className="font-display text-[2.6rem] leading-[1.08] md:text-5xl lg:text-6xl font-extrabold text-dark-900 tracking-tight">
                  Everything your desk needs, <span className="text-athletic-700">nothing it doesn&apos;t</span>
                </h1>
                <p className="mt-6 text-lg text-dark-500 max-w-xl leading-relaxed">Work Supply House brings together a small, dependable range of office supplies, electronics and furniture &mdash; chosen for quiet quality and shipped free across the USA.</p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/shop" className="inline-flex items-center gap-2 rounded-full bg-athletic-700 px-8 py-4 font-semibold text-white transition hover:bg-athletic-800">Shop Supplies <ArrowRight className="w-4 h-4" /></Link>
                  <Link to="/about" className="inline-flex items-center gap-2 rounded-full border border-dark-300 px-8 py-4 font-semibold text-dark-800 hover:border-athletic-500 hover:text-athletic-700 transition">Our Story</Link>
                </div>
                <div className="mt-9 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-dark-500">
                  <span className="flex items-center gap-2"><Truck className="w-4 h-4 text-athletic-600" /> Free USA shipping</span>
                  <span className="flex items-center gap-2"><RotateCcw className="w-4 h-4 text-athletic-600" /> 30-day returns</span>
                  <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-athletic-600" /> Secure checkout</span>
                </div>
              </div>
              <div className="relative">
                <div className="relative rounded-3xl overflow-hidden border border-dark-200 shadow-card">
                  <img src={siteImages.heroMain} alt="Calm, organized workspace" className="w-full h-[380px] lg:h-[520px] object-cover" />
                </div>
                <div className="hidden sm:flex absolute -bottom-6 -left-6 bg-white rounded-2xl border border-dark-200 shadow-elevated p-5 items-center gap-4 max-w-[260px]">
                  <div className="w-12 h-12 rounded-full bg-athletic-100 text-athletic-700 flex items-center justify-center flex-shrink-0"><Star className="w-6 h-6" /></div>
                  <div><p className="font-display text-dark-900 font-bold text-sm leading-tight">Curated, not cluttered</p><p className="text-dark-500 text-xs mt-0.5">A focused lineup you can trust</p></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust strip */}
        <section className="bg-white border-y border-dark-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => {
              const Icon = featureIcons[i % featureIcons.length];
              return (
                <div key={f.id} className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-full bg-athletic-50 text-athletic-700 flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5" /></div>
                  <div><p className="font-semibold text-dark-900 text-sm">{f.title}</p><p className="text-dark-500 text-xs mt-0.5">{f.description}</p></div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Categories — quiet cards */}
        <section className="bg-background py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10"><span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-athletic-600 mb-2">Shop by category</span><h2 className="font-display text-4xl font-extrabold text-dark-900 tracking-tight">Built around how you work</h2></div>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-5">
              {categories.map((category) => (
                <Link key={category.id} to={`/shop?category=${category.slug}`} className="group bg-white rounded-2xl overflow-hidden border border-dark-200 hover:border-athletic-300 hover:shadow-card-hover transition-all">
                  <div className="aspect-square bg-dark-50 overflow-hidden"><img src={category.image} alt={category.name} loading="lazy" className="w-full h-full object-contain p-6 group-hover:scale-105 transition-transform duration-500" /></div>
                  <div className="p-4 border-t border-dark-100">
                    <h3 className="font-display text-[0.95rem] font-bold text-dark-900 group-hover:text-athletic-700 transition-colors leading-tight">{category.name}</h3>
                    <p className="text-xs text-dark-400 mt-1">{category.productCount} product{category.productCount > 1 ? 's' : ''}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Bestsellers */}
        <section className="bg-white py-16 sm:py-20 border-y border-dark-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10">
              <div><span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-athletic-600 mb-2">Handpicked for your desk</span><h2 className="font-display text-4xl font-extrabold text-dark-900 tracking-tight">Quiet essentials</h2></div>
              <Link to="/shop" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-athletic-700 hover:gap-2.5 transition-all">View all <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">{productsGrid.map((p) => (<ProductCard key={p.id} product={p} />))}</div>
          </div>
        </section>

        {/* Featured promo split */}
        <section className="bg-background py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-last lg:order-first">
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-athletic-600 mb-3">Featured this week</span>
              <h2 className="font-display text-4xl font-extrabold text-dark-900 mb-4 tracking-tight">{featuredProduct.name}</h2>
              <p className="text-dark-500 mb-5 max-w-lg leading-relaxed">{featuredProduct.description.slice(0, 190)}&hellip;</p>
              <p className="text-3xl font-extrabold text-dark-900 mb-7">${featuredProduct.price.toFixed(2)}</p>
              <div className="flex flex-wrap gap-3">
                <Link to={`/product/${featuredProduct.slug}`} className="inline-flex items-center gap-2 px-7 py-3.5 bg-athletic-700 text-white rounded-full font-semibold hover:bg-athletic-800 transition">Buy now</Link>
                <Link to="/shop" className="inline-flex items-center gap-2 px-7 py-3.5 border border-dark-300 text-dark-800 rounded-full font-medium hover:border-athletic-500 hover:text-athletic-700 transition">Browse more</Link>
              </div>
            </div>
            <div className="rounded-3xl bg-white border border-dark-200 shadow-card p-8"><img src={featuredProduct.image} alt={featuredProduct.name} className="w-full max-w-md mx-auto object-contain" /></div>
          </div>
        </section>

        {/* Benefits band (sage) */}
        <section className="bg-gradient-athletic py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {performanceBenefits.map((b, i) => {
                const Icon = benefitIcons[i % benefitIcons.length];
                return (
                  <div key={b.id} className="text-center sm:text-left">
                    <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center text-white mb-4 mx-auto sm:mx-0"><Icon className="w-6 h-6" /></div>
                    <h3 className="font-display text-xl font-bold text-white">{b.title}</h3>
                    <p className="text-white/85 text-sm mt-1">{b.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="bg-white py-16 sm:py-20 border-b border-dark-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12"><span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-athletic-600 mb-2">How it works</span><h2 className="font-display text-4xl font-extrabold text-dark-900 tracking-tight">Set up your desk in four steps</h2></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {steps.map((step) => (
                <div key={step.id} className="bg-background border border-dark-200 rounded-2xl p-6"><span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-athletic-700 text-white font-display font-bold">{step.id}</span><h3 className="font-display text-xl font-bold text-dark-900 mt-4">{step.title}</h3><p className="text-dark-500 text-sm mt-2">{step.description}</p></div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-background py-16 sm:py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-4xl font-extrabold text-dark-900 text-center mb-10 tracking-tight">Questions, answered</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[
                { q: 'How fast will my order arrive?', a: `Orders ship via USPS within 1 business day and typically arrive in 2 to 4 business days. Need an update? Call us at ${CONTACT_INFO.phone}.` },
                { q: 'Do you charge for shipping?', a: 'Never. Every order ships free via USPS Standard Shipping within the USA, with no minimum spend.' },
                { q: 'What is your return policy?', a: 'You have 30 days from delivery to start a return. Reach out and our team will walk you through each step.' },
                { q: 'How can I get in touch?', a: `Our support team is here at ${CONTACT_INFO.phone} or by email at ${CONTACT_INFO.email}.` },
              ].map((faq, i) => (<div key={i} className="bg-white rounded-2xl p-6 border border-dark-200"><h3 className="font-display text-lg font-bold text-dark-900">{faq.q}</h3><p className="mt-2 text-dark-600 text-sm leading-relaxed">{faq.a}</p></div>))}
            </div>
            <div className="text-center mt-10"><Link to="/faq" className="inline-flex items-center gap-2 text-athletic-700 hover:text-athletic-800 font-semibold">See all FAQs <ArrowRight className="w-4 h-4" /></Link></div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default HomePage;
